import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "@emotion/styled";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Input from "../../UI/Form/Input/Input";
import IOButton from "../../UI/Form/Button/IOButton";
import Loading from "../../UI/Loading/Loading";
import AlertMessage from "../../UI/Alerts/AlertMessage";
import Flex from "../../UI/Flex/Flex";
import DateTime from "../../UI/DateTime";
import CopyToClipboard from "../../UI/CopyToClipboard/CopyToClipboard";
import useConfirmDelete from "../../hooks/useConfirmDelete";
import { apiEndpoint, frontendBaseUrl } from "../../utils/env";
import {
  createOAuthClient,
  deleteOAuthClient,
  listOAuthClients,
  OAuthClient,
} from "../../api/oauthClients";
import { showToastWithTimeout } from "../../store/actions/message";
import { faClipboard } from "@fortawesome/pro-regular-svg-icons";

const DEFAULT_REDIRECT_URL = "http://example.com";
const DEFAULT_TOKEN_EXPIRY = "P4M";
const USER_TOKEN_ENDPOINT_PATH = "/api/v1/oauth/user-token";

const PageContainer = styled.div`
  padding-right: 30px;
  @media (max-width: 800px) {
    padding: 0 30px;
  }
`;

const Breadcrumbs = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  font-size: 13px;
  margin: 10px 0 18px 0;

  a {
    color: ${(props) => props.theme.text.secondary};
    text-decoration: none;
  }

  a:hover {
    text-decoration: underline;
  }
`;

const Title = styled.div`
  font-size: larger;
  text-align: left;
  margin-bottom: 18px;
`;

const Card = styled.div`
  border: 1px solid ${(props) => props.theme.gray.gray300};
  border-radius: 6px;
  padding: 16px;
  max-width: 820px;
  background: ${(props) =>
    props.theme.type === "dark" ? props.theme.gray.gray900 : "white"};
`;

const Table = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 180px 2fr 120px;
  gap: 10px 14px;
  align-items: center;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const HeaderCell = styled.div`
  font-weight: 700;
  opacity: 0.85;
`;

const NameLink = styled(Link)`
  color: ${(props) => props.theme.text.primary};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const Mono = styled.div`
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace;
  user-select: all;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Divider = styled.div`
  height: 1px;
  background: ${(props) => props.theme.gray.gray300};
  margin: 16px 0;
`;

const SectionTitle = styled.div`
  font-weight: 700;
  opacity: 0.9;
  margin-bottom: 10px;
`;

const SecretFieldWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const SecretToggle = styled.button`
  position: absolute;
  right: 10px;
  top: 42px;
  transform: translateY(-50%);
  border: 0;
  background: transparent;
  padding: 6px;
  cursor: pointer;
  color: ${(props) => props.theme.text.secondary};

  &:focus {
    outline: none;
  }
`;

export default function OAuthClientsPage() {
  const [clients, setClients] = useState<OAuthClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingIds, setDeletingIds] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{
    name?: string[];
    redirect?: string[];
  }>({});

  const [name, setName] = useState("");
  const [redirect, setRedirect] = useState("");
  const [created, setCreated] = useState<{ id: number; secret: string } | null>(
    null
  );
  const [showCreatedPanel, setShowCreatedPanel] = useState(false);
  const [showCreatedSecret, setShowCreatedSecret] = useState(false);

  const [tokenClientId, setTokenClientId] = useState("");
  const [tokenClientSecret, setTokenClientSecret] = useState("");
  const [showTokenClientSecret, setShowTokenClientSecret] = useState(false);
  const [showTokenCurlTool, setShowTokenCurlTool] = useState(false);

  const sortedClients = useMemo(
    () => [...clients].sort((a, b) => b.id - a.id),
    [clients]
  );

  const userTokenCurl = useMemo(() => {
    const base = (apiEndpoint || frontendBaseUrl || "").replace(/\/$/, "");
    const url = `${base}${USER_TOKEN_ENDPOINT_PATH}`;
    const payload = {
      client_id: tokenClientId.trim(),
      client_secret: tokenClientSecret.trim(),
      expiry: DEFAULT_TOKEN_EXPIRY,
    };

    return `curl -sS -X POST "${url}" -H "Content-Type: application/json" -H "Accept: application/json" -d '${JSON.stringify(
      payload
    )}'`;
  }, [tokenClientId, tokenClientSecret]);

  const load = useCallback(async (signal?: AbortSignal) => {
    setError(null);
    setLoading(true);
    try {
      const next = await listOAuthClients(signal);
      setClients(next);
    } catch (e: any) {
      if (e?.name === "CanceledError") {
        return;
      }
      setError(
        e?.response?.data?.message ??
          e?.message ??
          "Failed to load OAuth clients."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const onCreate = async (evt: React.FormEvent) => {
    evt.preventDefault();
    setSaving(true);
    setError(null);
    setFieldErrors({});
    setCreated(null);
    try {
      const normalizedRedirect = redirect.trim();
      const res = await createOAuthClient({
        name,
        redirect: normalizedRedirect || DEFAULT_REDIRECT_URL,
      });
      setCreated({ id: res.id, secret: res.secret });
      setShowCreatedPanel(true);
      setShowCreatedSecret(false);
      setShowTokenCurlTool(true);
      setTokenClientId(String(res.id));
      setTokenClientSecret(res.secret);
      setShowTokenClientSecret(false);
      setName("");
      setRedirect("");
      await load();
      showToastWithTimeout("OAuth client created.", "success");
    } catch (e: any) {
      const validationErrors =
        e?.response?.status === 422 ? e?.response?.data?.errors : null;
      if (validationErrors) {
        const message =
          e?.response?.data?.message ??
          "Please fix the highlighted validation errors.";
        setError(message);
        showToastWithTimeout(message, "danger");
        setFieldErrors({
          name: validationErrors.name,
          // Some Laravel validators may use redirect_url; support both.
          redirect: validationErrors.redirect ?? validationErrors.redirect_url,
        });
        return;
      }
      const message =
        e?.response?.data?.message ??
        e?.message ??
        "Failed to create OAuth client.";
      setError(message);
      showToastWithTimeout(message, "danger");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (client: OAuthClient) => {
    setError(null);
    setCreated(null);
    setDeletingIds((prev) => new Set(prev).add(client.id));
    try {
      await deleteOAuthClient(client.id);
      await load();
      showToastWithTimeout("OAuth client deleted.", "success");
    } catch (e: any) {
      const message =
        e?.response?.data?.message ??
        e?.message ??
        "Failed to delete OAuth client.";
      setError(message);
      showToastWithTimeout(message, "danger");
    } finally {
      setDeletingIds((prev) => {
        const next = new Set(prev);
        next.delete(client.id);
        return next;
      });
    }
  };

  const confirmDelete = useConfirmDelete(
    onDelete,
    "Delete OAuth client?",
    "This will permanently delete the OAuth client and cannot be undone."
  );

  return (
    <PageContainer data-cy="oauth-clients-page">
      <Breadcrumbs data-cy="oauth-clients-breadcrumbs">
        <Link to="/user-profile">User Profile</Link>
        <span>/</span>
        <span>OAuth Clients</span>
      </Breadcrumbs>

      <Title>OAuth Clients</Title>

      {error ? (
        <AlertMessage status="error" cy="oauth-clients-error">
          {error}
        </AlertMessage>
      ) : null}

      {created && showCreatedPanel ? (
        <AlertMessage
          status="success"
          cy="oauth-client-created"
          stacked
          outline
          actions={null}
        >
          <Flex justifyContent="space-between" alignItems="center" gap={12}>
            <div style={{ fontWeight: 700 }}>
              Client created — copy the secret now (it won’t be shown again)
            </div>
            <IOButton
              type="button"
              outline
              smallPadding
              cy="oauth-client-created-close"
              onClick={() => setShowCreatedPanel(false)}
              title="Close"
            >
              <FontAwesomeIcon icon={["fas", "times"]} />
            </IOButton>
          </Flex>
          <div style={{ marginBottom: 10 }}>
            <div style={{ opacity: 0.8, marginBottom: 4 }}>Client ID</div>
            <Mono>{created.id}</Mono>
          </div>
          <div>
            <Flex justifyContent="space-between" alignItems="center">
              <div style={{ opacity: 0.8, marginBottom: 4 }}>Client Secret</div>
              <Flex alignItems="center" gap={10}>
                <CopyToClipboard
                  text={created.secret}
                  onCopy={(_text, didCopy) => {
                    if (didCopy) {
                      showToastWithTimeout("Client secret copied.", "success");
                    } else {
                      showToastWithTimeout(
                        "Failed to copy to clipboard.",
                        "danger"
                      );
                    }
                  }}
                >
                  <IOButton
                    type="button"
                    outline
                    smallPadding
                    cy="oauth-client-created-copy-secret"
                  >
                    <FontAwesomeIcon icon={["fas", "copy"]} />
                  </IOButton>
                </CopyToClipboard>
                <IOButton
                  type="button"
                  outline
                  smallPadding
                  cy="oauth-client-created-toggle-secret"
                  title="Toggle Reveal"
                  onClick={() => setShowCreatedSecret((v) => !v)}
                >
                  <FontAwesomeIcon
                    icon={["fas", showCreatedSecret ? "eye-slash" : "eye"]}
                  />
                </IOButton>
              </Flex>
            </Flex>
            <Mono>{showCreatedSecret ? created.secret : "********"}</Mono>
          </div>
        </AlertMessage>
      ) : null}

      <Card style={{ marginBottom: 16 }}>
        <form onSubmit={onCreate} data-cy="oauth-client-create-form">
          <Flex direction="column" gap={12}>
            <Input
              label="Client name"
              value={name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setName(e.target.value);
                if (fieldErrors.name?.length) {
                  setFieldErrors((prev) => ({ ...prev, name: undefined }));
                }
              }}
              required
              invalid={Boolean(fieldErrors.name?.length)}
              errorMessage={fieldErrors.name}
              cy="oauth-client-name"
            />
            <Input
              label="Redirect URL"
              value={redirect}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setRedirect(e.target.value);
                if (fieldErrors.redirect?.length) {
                  setFieldErrors((prev) => ({ ...prev, redirect: undefined }));
                }
              }}
              placeholder="https://example.com/oauth/callback"
              invalid={Boolean(fieldErrors.redirect?.length)}
              errorMessage={fieldErrors.redirect}
              cy="oauth-client-redirect"
            />
            <Flex justifyContent="flex-end">
              <IOButton
                type="submit"
                processing={saving}
                disabled={!name || saving}
                cy="oauth-client-create-submit"
                smallPadding
              >
                Create Client
              </IOButton>
            </Flex>
          </Flex>
        </form>

        <Divider />

        <Flex justifyContent="space-between" alignItems="center">
          <SectionTitle data-cy="oauth-user-token-curl-title">
            Create User Token (cURL)
          </SectionTitle>
          <IOButton
            type="button"
            outline
            smallPadding
            cy="oauth-user-token-toggle"
            onClick={() => setShowTokenCurlTool((v) => !v)}
          >
            {showTokenCurlTool ? "Hide" : "Show"}
          </IOButton>
        </Flex>

        {showTokenCurlTool ? (
          <Flex
            direction="column"
            gap={12}
            data-cy="oauth-user-token-curl-tool"
          >
            <Input
              label="Client ID"
              value={tokenClientId}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setTokenClientId(e.target.value)
              }
              name="oauth_user_token_client_id"
              autoComplete="off"
              cy="oauth-user-token-client-id"
              placeholder="e.g. 12"
              fit
            />
            <SecretFieldWrapper>
              <Input
                label="Client Secret"
                value={tokenClientSecret}
                type={showTokenClientSecret ? "text" : "password"}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setTokenClientSecret(e.target.value)
                }
                name="oauth_user_token_client_secret"
                autoComplete="new-password"
                cy="oauth-user-token-client-secret"
                placeholder="Paste client secret"
                style={{ paddingRight: 40 }}
                fit
              />
              <SecretToggle
                type="button"
                aria-label={
                  showTokenClientSecret
                    ? "Hide client secret"
                    : "Show client secret"
                }
                onClick={() => setShowTokenClientSecret((v) => !v)}
                data-cy="oauth-user-token-client-secret-toggle"
              >
                <FontAwesomeIcon
                  icon={["fas", showTokenClientSecret ? "eye-slash" : "eye"]}
                />
              </SecretToggle>
            </SecretFieldWrapper>

            <Flex justifyContent="flex-end" alignItems="center" gap={10}>
              <CopyToClipboard
                text={userTokenCurl}
                title="On Windows you should still be able to import this cURL command into Postman."
                onCopy={(_text, didCopy) => {
                  if (didCopy) {
                    showToastWithTimeout("cURL command copied.", "success");
                  } else {
                    showToastWithTimeout(
                      "Failed to copy to clipboard.",
                      "danger"
                    );
                  }
                }}
              >
                <IOButton
                  type="button"
                  smallPadding
                  cy="oauth-user-token-copy-curl"
                  icon={faClipboard}
                  disabled={!tokenClientId.trim() || !tokenClientSecret.trim()}
                >
                  Copy Linux/macOS cURL to Clipboard
                </IOButton>
              </CopyToClipboard>
            </Flex>
          </Flex>
        ) : null}
      </Card>

      <Card>
        {loading ? (
          <div style={{ padding: 18 }}>
            <Loading />
          </div>
        ) : sortedClients.length ? (
          <>
            <Table data-cy="oauth-client-list">
              <HeaderCell>Name</HeaderCell>
              <HeaderCell>Created</HeaderCell>
              <HeaderCell>Redirect</HeaderCell>
              <HeaderCell />

              {sortedClients.map((c) => {
                const isDeleting = deletingIds.has(c.id);
                const displayRedirect =
                  c.redirect === DEFAULT_REDIRECT_URL ? "" : c.redirect;
                return (
                  <React.Fragment key={c.id}>
                    <div data-cy="oauth-client-name-value">
                      <NameLink to={`/user-profile/oauth/clients/${c.id}`}>
                        {c.name}
                      </NameLink>
                    </div>
                    <div data-cy="oauth-client-created-at-value">
                      <DateTime date={c.created_at} />
                    </div>
                    <Mono
                      title={displayRedirect}
                      data-cy="oauth-client-redirect-value"
                    >
                      {displayRedirect}
                    </Mono>
                    <div>
                      <IOButton
                        type="button"
                        outline
                        error
                        smallPadding
                        processing={isDeleting}
                        disabled={isDeleting}
                        onClick={() => confirmDelete.setConfirming(c)}
                        cy="oauth-client-delete"
                      >
                        Delete
                      </IOButton>
                    </div>
                    <Divider style={{ gridColumn: "1 / -1", margin: 6 }} />
                  </React.Fragment>
                );
              })}
            </Table>
          </>
        ) : (
          <div data-cy="oauth-client-empty" style={{ opacity: 0.8 }}>
            You don’t have any OAuth clients yet.
          </div>
        )}
      </Card>

      {confirmDelete.confirming}
    </PageContainer>
  );
}
